// oxlint-disable-next-line no-restricted-imports
import { Combobox as MantineCombobox, InputBase, useCombobox } from "@mantine/core";
import { IconCheck, IconChevronDown, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { lineHeights } from "#app/theme/v2/lineHeights";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Divider } from "#components/atoms/Divider/Divider";
import { Text } from "#components/atoms/Typographies/Text";

import classes from "./Combobox.module.css";

type ComboboxProps = {
  label?: string;
  placeholder?: string;
  data: { value: string; label: string }[] | string[];
  error?: string;
  disabled?: boolean;
  required?: boolean;
  value?: string | null;
  onChange?: (value: string | null) => void;
  hint?: string;
  nothingFoundMessage?: string;
  onCreateOption: (value: string) => void;
  createOptionLabel?: (search: string) => string;
};

const normalizeData = (
  data: { value: string; label: string }[] | string[],
): { value: string; label: string }[] =>
  data.map((item) => (typeof item === "string" ? { value: item, label: item } : item));

const Combobox = ({
  label,
  placeholder,
  data,
  error,
  disabled,
  required,
  value,
  onChange,
  hint,
  nothingFoundMessage,
  onCreateOption,
  createOptionLabel,
}: ComboboxProps) => {
  const { t } = useTranslation("components");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  const [search, setSearch] = useState("");

  const normalizedData = normalizeData(data);
  const selectedLabel = normalizedData.find((item) => item.value === value)?.label ?? "";

  const filteredOptions = normalizedData.filter((item) =>
    item.label.toLowerCase().includes(search.toLowerCase().trim()),
  );

  const exactMatch = normalizedData.some(
    (item) => item.label.toLowerCase() === search.toLowerCase().trim(),
  );

  const showCreateOption = search.trim().length > 0 && !exactMatch;

  const createLabel = createOptionLabel?.(search) ?? t("select.create", { value: search });

  const handleOptionSubmit = (val: string) => {
    if (val === "__create__") {
      onCreateOption(search.trim());
      setSearch("");
    } else {
      const found = normalizedData.find((item) => item.value === val);
      onChange?.(val === value ? null : val);
      setSearch(found?.label ?? "");
    }
    combobox.closeDropdown();
  };

  const displayValue = combobox.dropdownOpened ? search : selectedLabel;

  return (
    <MantineCombobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      shadow="none"
      radius={radius.lg}
      classNames={{
        dropdown: classes.dropdown,
        option: classes.option,
      }}
    >
      <MantineCombobox.Target>
        <InputBase
          label={label}
          placeholder={placeholder}
          error={error}
          disabled={disabled}
          required={required}
          value={displayValue}
          rightSection={<IconChevronDown size={16} color={colors.icon.primaryDefault} />}
          rightSectionPointerEvents="none"
          pointer={!combobox.dropdownOpened}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            combobox.openDropdown();
            combobox.resetSelectedOption();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => {
            combobox.openDropdown();
            setSearch("");
          }}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch("");
          }}
          classNames={{
            label: classes.label,
            input: classes.input,
            section: classes.section,
          }}
          style={
            {
              "--select-label-fz": fontSizes[14],
              "--select-label-fw": fontWeights.medium,
              "--select-label-lh": lineHeights[1.25],
              "--select-label-c": colors.text.primaryDefault,
              "--select-label-mb": spacing[1],
              "--select-input-fz": fontSizes[14],
              "--select-input-fw": fontWeights.regular,
              "--select-input-h": spacing[9],
              "--select-input-radius": radius.sm,
              "--select-input-bg": disabled
                ? colors.bg.elevation.tertiary
                : colors.bg.elevation.primary,
              "--select-input-bd": error ? colors.border.error.primary : colors.border.tertiary,
              "--select-input-c": colors.text.primaryDefault,
              "--select-placeholder-c": colors.text.secondaryDefault,
              "--select-focus-bd": colors.border.primaryDefault,
              "--select-error-bd": colors.border.error.primary,
              "--select-disabled-bg": colors.bg.elevation.tertiary,
              "--select-disabled-c": colors.text.primaryDisabled,
            } as React.CSSProperties
          }
        />
      </MantineCombobox.Target>

      <MantineCombobox.Dropdown>
        {hint && (
          <Text
            size="tiny"
            type="medium"
            color={colors.text.secondaryDefault}
            style={{ padding: `${spacing[2]} ${spacing[2]} ${spacing[2]}`, width: "100%" }}
          >
            {hint}
          </Text>
        )}
        <MantineCombobox.Options>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filteredOptions.length === 0 && !showCreateOption ? (
              <MantineCombobox.Empty>
                {nothingFoundMessage ?? t("select.nothingFound")}
              </MantineCombobox.Empty>
            ) : (
              filteredOptions.map((item) => (
                <MantineCombobox.Option
                  key={item.value}
                  value={item.value}
                  active={item.value === value}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>{item.label}</span>
                    {item.value === value && (
                      <IconCheck size={16} color={colors.icon.primaryDefault} />
                    )}
                  </div>
                </MantineCombobox.Option>
              ))
            )}
          </div>

          {showCreateOption && (
            <>
              <div style={{ padding: `${spacing[2]} 0` }}>
                <Divider />
              </div>
              <MantineCombobox.Option value="__create__" className={classes.createOption}>
                <IconPlus size={14} color={colors.icon.primaryDefault} />
                <span>{createLabel}</span>
              </MantineCombobox.Option>
            </>
          )}
        </MantineCombobox.Options>
      </MantineCombobox.Dropdown>
    </MantineCombobox>
  );
};

export { Combobox };
